import React, { useState, useRef, useCallback } from 'react';
import { useClickOutside } from '@/core/hooks/useClickOutside';
import { useDropdownAutoScroll } from '@/core/hooks/useDropdownAutoScroll';
import './BaseSelect.scss';

const BdsIcon = 'bds-icon' as any;

export interface SelectOption {
  label: string;
  value: string;
}

export interface BaseSelectProps {
  options: SelectOption[];
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  className?: string;
  alwaysOpen?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  iconSize?: 'xxx-large' | 'xx-large' | 'x-large' | 'large' | 'medium' | 'small' | 'x-small' | 'xx-small' | 'xxx-small';
}

export const BaseSelect: React.FC<BaseSelectProps> = ({
  options,
  value,
  placeholder = '',
  onChange,
  className = '',
  alwaysOpen = false,
  isOpen: externalIsOpen,
  onOpenChange,
  disabled = false,
  iconSize = 'small'
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = externalIsOpen !== undefined;
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const setIsOpen = useCallback((open: boolean) => {
    if (!isControlled) {
      setInternalIsOpen(open);
    }
    onOpenChange?.(open);
  }, [isControlled, onOpenChange]);

  const close = useCallback(() => setIsOpen(false), [setIsOpen]);

  useClickOutside(containerRef, close, isOpen);

  const handleToggle = () => setIsOpen(!isOpen);

  const handleSelect = (optionValue: string) => {
    setIsOpen(false);
    onChange?.(optionValue);
  };

  const selectedLabel = options.find(opt => opt.value === value)?.label || placeholder;
  const showDropdown = alwaysOpen || isOpen;

  useDropdownAutoScroll(showDropdown, dropdownRef, []);

  return (
    <div ref={containerRef} className={`base-select ${className}`.trim()}>
      {!alwaysOpen && (
        <button
          type="button"
          className={`base-select__trigger ${isOpen ? 'base-select__trigger--open' : ''}`}
          onClick={handleToggle}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          disabled={disabled}
        >
          <span className="base-select__trigger__text">{selectedLabel}</span>
          <span className="base-select__trigger__icon" aria-hidden="true">
            <span className={`base-select__trigger__rotation ${isOpen ? 'base-select__trigger__rotation--open' : ''}`}>
              <BdsIcon name="arrow-down" size={iconSize} />
            </span>
          </span>
        </button>
      )}

      {showDropdown && (
        <div className="base-select__dropdown" ref={dropdownRef}>
          <ul className="base-select__list">
            {options.map(option => (
              <li key={option.value} className="base-select__list__item">
                <button
                  type="button"
                  className={`base-select__option ${value === option.value ? 'base-select__option--selected' : ''}`}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};